// Per-device language persistence for the admin portal (take-order-i18n spec,
// Requirements 3.3, 3.7, 3.9).
//
// Language is a PER-DEVICE, PER-USER choice, not a tenant setting: the tenant
// declares which languages are offered, and each staff member picks one on their
// own terminal. One waiter runs Tamil while a colleague at the next station runs
// English against the same tenant.
//
// Keys are tenant-scoped (Requirement 3.9). One admin-portal deployment serves a
// single tenant, so this is close to vacuous on web — it exists so the key shape
// matches the mobile app, where tenant switching is real, and so a shared-origin
// deployment could never leak a language selection between tenants.
//
// EVERY OPERATION FAILS SOFT (Requirement 3.7). localStorage throws in Safari
// private mode, when a quota is exceeded, and when storage is disabled by policy.
// A read failure yields null so the caller falls back to the tenant default; a
// write failure is swallowed. Losing a language preference across a reload is a
// nuisance, not a service outage, and must never surface an error to a waiter
// mid-shift or block the Take Order screen from rendering.

const KEY_PREFIX = 'ezsell.takeOrder.lang';

// A tenant identifier is required, so a missing one cannot silently collapse
// every tenant onto one shared key.
function keyFor(tenantId) {
  const id = String(tenantId || '').trim();
  return id ? `${KEY_PREFIX}.${id}` : '';
}

/**
 * Read the language this device last selected for the given tenant.
 * Returns null when absent, when the tenant id is empty, or on any failure.
 * The caller is responsible for validating the value against the tenant's
 * available languages — a stored code may name a language the tenant has since
 * removed (Requirement 3.5).
 */
export function readDeviceLanguage(tenantId) {
  const key = keyFor(tenantId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    const value = String(raw || '').trim().toLowerCase();
    return value || null;
  } catch {
    return null;
  }
}

/**
 * Persist the language this device selected for the given tenant.
 * Silent no-op on failure, and for an empty tenant id or empty code.
 */
export function writeDeviceLanguage(tenantId, code) {
  const key = keyFor(tenantId);
  const value = String(code || '').trim().toLowerCase();
  if (!key || !value) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Non-fatal by design — see the header note.
  }
}

/** Remove this device's stored selection for the given tenant. */
export function clearDeviceLanguage(tenantId) {
  const key = keyFor(tenantId);
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Non-fatal by design.
  }
}
